// src/components/apps/Licenses/LicensesPage.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import LicensesTable from './LicensesTable';
import LicensesFormModal from './LicensesFormModal';
import LicensesMatrix from './LicensesMatrix';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { useAuth } from '../../../context/AuthContext';

const LicensesPage = () => {
  const { apiFetch } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [refresh, setRefresh] = useState(false);

  // NEW: parent-owned data for matrix
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const res = await apiFetch(API_ENDPOINTS.SOFTWARE_LICENSES);
      const data = await res.json();
      setLicenses(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load licenses:', e);
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  };

  // load once + whenever table refresh flips
  useEffect(() => {
    fetchLicenses();
  }, [refresh]);

  const handleAdd = () => {
    setSelectedLicense(null);
    setModalOpen(true);
  };

  const handleSuccess = () => {
    setModalOpen(false);
    setRefresh(prev => !prev); // triggers table & matrix refresh
  };

  const handleCloseModal = () => setModalOpen(false);

  const handleEdit = (license) => {
    setSelectedLicense(license);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this license?')) return;
    try {
      await apiFetch(`${API_ENDPOINTS.SOFTWARE_LICENSES}${id}/`, {
        method: 'DELETE',
      });
      setRefresh(prev => !prev); // triggers table & matrix refresh
    } catch (error) {
      console.error('Failed to delete license:', error);
    }
  };

  const applyLocalToggle = (id, field, value) => {
    setLicenses(prev =>
      prev.map(l => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  return (
    <Box component="main" sx={{ mt: 4 }}>
      <Box
        sx={{
          backgroundColor: '#f9f9f9',
          p: 3,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          mt: '50px',
          mb: 4,
          textAlign: 'center',
          transition: 'transform 0.3s ease, boxShadow 0.3s ease',
          '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 8px 16px rgba(0,0,0,0.2)' },
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: 'primary.main' }}>
          Applications Management
        </Typography>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Button variant="contained" onClick={handleAdd}>+ Add App</Button>
        </Paper>

        <Paper sx={{ p: 3, mb: 4 }}>
          <LicensesTable
              licenses={licenses}          // ✅ pass data
              loading={loading}            // ✅ pass loading
              reload={fetchLicenses}       // ✅ so uploader can trigger a refresh
              onLocalToggle={applyLocalToggle}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
        </Paper>

        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <LicensesMatrix licenses={licenses} />
          )}
        </Paper>
      </Box>

      <LicensesFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        selectedLicense={selectedLicense}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default LicensesPage;